import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Copy, RefreshCcw, Trash2, Plus, CalendarSync, CheckCircle2, AlertTriangle, XCircle, Globe, ExternalLink,
} from "lucide-react";

const SOURCE_OPTIONS = ["Airbnb", "Booking", "Vrbo", "Outro"];

function hoursAgo(iso) {
  if (!iso) return null;
  const h = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  return h;
}

function fmtRel(iso) {
  if (!iso) return "nunca";
  const h = hoursAgo(iso);
  if (h < 1) return `há ${Math.max(1, Math.round(h * 60))} min`;
  if (h < 24) return `há ${Math.round(h)} h`;
  return `há ${Math.round(h / 24)} dias`;
}

function feedStatus(f) {
  if (f.last_error) return { key: "err", label: "Falha", color: "text-rose-700 bg-rose-50 border-rose-200", Icon: XCircle };
  const h = hoursAgo(f.last_success_at);
  if (h === null) return { key: "new", label: "Aguardando 1ª sync", color: "text-slate-600 bg-slate-50 border-slate-200", Icon: RefreshCcw };
  if (h >= 6) return { key: "stale", label: `Sem sync ${fmtRel(f.last_success_at)}`, color: "text-amber-800 bg-amber-50 border-amber-200", Icon: AlertTriangle };
  return { key: "ok", label: `Sincronizado ${fmtRel(f.last_success_at)}`, color: "text-emerald-800 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 };
}

export default function ICalSyncManager({ houseId, houseName }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null); // {export_url, export_token, slug, feeds}
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("Airbnb");
  const [adding, setAdding] = useState(false);
  const [busyFeedId, setBusyFeedId] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [rotating, setRotating] = useState(false);

  async function reload() {
    try {
      const r = await api.get(`/admin/houses/${houseId}/ical`);
      setConfig(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar configuração iCal");
    } finally { setLoading(false); }
  }

  useEffect(() => { reload(); }, [houseId, reload]);

  async function copyExportUrl() {
    if (!config?.export_url) return;
    try { await navigator.clipboard.writeText(config.export_url); toast.success("URL copiada"); }
    catch { toast.error("Não foi possível copiar — copie manualmente do campo."); }
  }

  async function rotateToken() {
    if (!window.confirm("Rotacionar o token vai invalidar a URL atual — você precisará cadastrar a nova URL em Airbnb/Booking/Vrbo. Continuar?")) return;
    setRotating(true);
    try {
      await api.post(`/admin/houses/${houseId}/ical/rotate-token`);
      toast.success("Token rotacionado. Nova URL gerada.");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao rotacionar token");
    } finally { setRotating(false); }
  }

  async function addFeed() {
    if (!newUrl.trim()) { toast.error("Cole a URL do calendário externo."); return; }
    setAdding(true);
    try {
      await api.post(`/admin/houses/${houseId}/ical/feeds`, { url: newUrl.trim(), source_label: newLabel });
      toast.success("Feed adicionado. Primeira sincronização já disparada.");
      setNewUrl(""); setNewLabel("Airbnb");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao adicionar feed");
    } finally { setAdding(false); }
  }

  async function syncFeed(feed) {
    setBusyFeedId(feed.id);
    try {
      const r = await api.post(`/admin/ical/feeds/${feed.id}/sync-now`);
      if (r.data?.ok) {
        toast.success(`Sync ok: ${r.data.created} novo(s), ${r.data.updated} atualizado(s), ${r.data.removed} removido(s)`);
      } else {
        toast.error(`Sync com erro: ${r.data?.error || "desconhecido"}`);
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na sincronização");
    } finally { setBusyFeedId(null); }
  }

  async function confirmRemove(keepBlockings) {
    if (!removeTarget) return;
    const id = removeTarget.id;
    setBusyFeedId(id);
    setRemoveTarget(null);
    try {
      await api.delete(`/admin/ical/feeds/${id}?keep_blockings=${keepBlockings ? "true" : "false"}`);
      toast.success(keepBlockings ? "Feed removido, bloqueios mantidos como manuais." : "Feed removido, bloqueios movidos para a Lixeira.");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao remover feed");
    } finally { setBusyFeedId(null); }
  }

  if (loading) return <div className="text-xs text-slate-500">Carregando configuração iCal…</div>;

  return (
    <div data-testid="ical-sync-manager">
      <div className="flex items-center gap-2 mb-1">
        <CalendarSync className="w-4 h-4 text-[#0AA89C]" />
        <div className="text-sm font-semibold">Sincronização iCal</div>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Exporte para Airbnb/Booking/Vrbo (URL única com token) e importe deles de volta.
        Sincronização automática a cada 1 hora. <strong>Cadastre a URL de exportação abaixo em cada plataforma externa manualmente.</strong>
      </p>

      {/* Export block */}
      <div className="p-3 rounded border border-[#0AA89C]/20 bg-[#0AA89C]/5 mb-5" data-testid="ical-export-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-medium text-[#22302E] flex items-center gap-1">
            <Globe className="w-3.5 h-3.5" /> URL de exportação (pública, com token secreto)
          </div>
          <Button type="button" variant="outline" size="sm" onClick={rotateToken} disabled={rotating} data-testid="ical-rotate-token">
            <RefreshCcw className="w-3.5 h-3.5 mr-1" /> {rotating ? "Rotacionando…" : "Rotacionar token"}
          </Button>
        </div>
        <div className="flex gap-2">
          <Input value={config.export_url} readOnly className="text-xs font-mono bg-white" data-testid="ical-export-url" />
          <Button type="button" variant="outline" size="sm" onClick={copyExportUrl} data-testid="ical-copy-url">
            <Copy className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-[11px] text-slate-500 mt-2">
          Se essa URL vazar, clique em <strong>Rotacionar token</strong> — a antiga fica inválida imediatamente.
        </p>
      </div>

      {/* Import block */}
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs font-medium text-[#22302E]">Calendários externos importados</div>
        <span className="text-[11px] text-slate-500">{config.feeds.length} feed{config.feeds.length !== 1 ? "s" : ""}</span>
      </div>

      {/* Feeds list */}
      {config.feeds.length === 0 ? (
        <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded p-3 mb-3">
          Nenhum feed importado ainda. Adicione o link .ics de Airbnb, Booking ou Vrbo dessa casa abaixo.
        </div>
      ) : (
        <div className="space-y-2 mb-3">
          {config.feeds.map((f) => {
            const st = feedStatus(f);
            return (
              <div key={f.id} className={`p-3 rounded border ${st.color}`} data-testid={`ical-feed-item-${f.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <st.Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="font-medium text-sm">{f.source_label}</span>
                      <span className="text-[10px] uppercase tracking-widest">{st.label}</span>
                    </div>
                    <a href={f.url} target="_blank" rel="noreferrer"
                       className="block text-[11px] font-mono text-slate-600 hover:text-[#0AA89C] truncate mt-1"
                       title={f.url}>
                      {f.url} <ExternalLink className="w-3 h-3 inline" />
                    </a>
                    {f.last_error && (
                      <div className="text-[11px] text-rose-700 mt-1" data-testid={`ical-feed-error-${f.id}`}>
                        Erro: {f.last_error}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-1">
                      Última tentativa: {fmtRel(f.last_synced_at)} · Eventos importados: {f.events_imported_count || 0}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button type="button" variant="outline" size="sm"
                            onClick={() => syncFeed(f)} disabled={busyFeedId === f.id}
                            data-testid={`ical-sync-now-${f.id}`}>
                      <RefreshCcw className={`w-3.5 h-3.5 ${busyFeedId === f.id ? "animate-spin" : ""}`} />
                    </Button>
                    <Button type="button" variant="outline" size="sm"
                            onClick={() => setRemoveTarget(f)} disabled={busyFeedId === f.id}
                            className="text-rose-700 border-rose-200 hover:bg-rose-50"
                            data-testid={`ical-remove-feed-${f.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add feed form */}
      <div className="p-3 rounded border border-slate-200 bg-slate-50" data-testid="ical-add-feed-form">
        <div className="text-xs font-medium mb-2">Adicionar novo feed</div>
        <div className="grid grid-cols-[130px_minmax(0,1fr)_auto] gap-2 items-end">
          <div>
            <Label className="text-[11px]">Origem</Label>
            <Select value={newLabel} onValueChange={setNewLabel}>
              <SelectTrigger data-testid="ical-add-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">URL .ics</Label>
            <Input
              value={newUrl} onChange={(e) => setNewUrl(e.target.value)}
              placeholder="https://www.airbnb.com/calendar/ical/..."
              className="text-xs font-mono"
              data-testid="ical-add-url"
            />
          </div>
          <Button
            type="button" onClick={addFeed} disabled={adding}
            className="bg-[#0AA89C] hover:bg-[#088c82] text-white"
            data-testid="ical-add-submit"
          >
            <Plus className="w-4 h-4 mr-1" /> {adding ? "Adicionando…" : "Adicionar"}
          </Button>
        </div>
      </div>

      {/* Remove confirmation modal */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="ical-remove-modal">
          <DialogHeader>
            <DialogTitle>Remover feed {removeTarget?.source_label}?</DialogTitle>
            <DialogDescription>
              O feed <span className="font-mono text-[11px]">{removeTarget?.url}</span> será
              desativado. Escolha o que fazer com os bloqueios que ele já importou para
              <strong> {houseName}</strong>:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <button
              type="button"
              onClick={() => confirmRemove(false)}
              className="w-full text-left p-3 rounded border border-slate-200 hover:border-[#0AA89C] hover:bg-[#0AA89C]/5 transition"
              data-testid="ical-remove-with-blockings"
            >
              <div className="font-medium text-sm">Remover feed E os bloqueios importados</div>
              <div className="text-xs text-slate-600 mt-1">
                Use se essa casa saiu desta plataforma. Os bloqueios vão para a Lixeira
                (recuperáveis por 30 dias).
              </div>
              <div className="text-[11px] text-amber-800 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Risco: se ainda houver reservas ativas naquela plataforma, elas podem
                virar overbooking até você desativar o calendário por lá também.
              </div>
            </button>
            <button
              type="button"
              onClick={() => confirmRemove(true)}
              className="w-full text-left p-3 rounded border border-slate-200 hover:border-[#0AA89C] hover:bg-[#0AA89C]/5 transition"
              data-testid="ical-remove-keep-blockings"
            >
              <div className="font-medium text-sm">Remover feed, MANTER bloqueios (como manuais)</div>
              <div className="text-xs text-slate-600 mt-1">
                Use se está apenas trocando a URL ou reconectando o feed. Os bloqueios
                permanecem no calendário como bloqueios manuais órfãos — a equipe pode
                editar/remover pela tela de Calendário depois.
              </div>
              <div className="text-[11px] text-amber-800 mt-1 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                Risco: se você esquecer de reconectar, o calendário externo pode acabar
                mostrando datas livres que na verdade estão bloqueadas — perda de disponibilidade real.
              </div>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} data-testid="ical-remove-cancel">
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
